import { useEffect, useRef, useState } from 'react';

/**
 * Waveform visualizer synced with the <audio> element used by PlayAlong.
 * - Decodes audio once via Web Audio API to compute peak buckets.
 * - Draws peaks on a canvas + a moving playhead based on audio.currentTime.
 * - Click / drag on the canvas to seek the audio element.
 * - Optional highlighted loop region between loopStartTime / loopEndTime (seconds).
 */
export default function Waveform({
  audioUrl,
  audioRef,
  loopStartTime = null,
  loopEndTime = null,
  seekDisabled = false,
  onSeek,
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const peaksRef = useRef(null);
  const durationRef = useRef(0);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  // Decode audio → peaks (1 pass, cached)
  useEffect(() => {
    if (!audioUrl) return;
    let cancelled = false;
    setStatus('loading');
    peaksRef.current = null;

    (async () => {
      try {
        const res = await fetch(audioUrl);
        if (!res.ok) throw new Error('fetch failed');
        const buf = await res.arrayBuffer();
        if (cancelled) return;
        const AC = window.AudioContext || window.webkitAudioContext;
        const ctx = new AC();
        const audio = await ctx.decodeAudioData(buf);
        if (cancelled) { ctx.close().catch(() => {}); return; }
        durationRef.current = audio.duration;

        // Compute ~1200 peak buckets (min/max per bucket) from channel 0.
        const targetBuckets = 1200;
        const channel = audio.getChannelData(0);
        const bucketSize = Math.max(1, Math.floor(channel.length / targetBuckets));
        const peaks = new Float32Array(targetBuckets * 2);
        for (let b = 0; b < targetBuckets; b++) {
          let mn = 0, mx = 0;
          const start = b * bucketSize;
          const end = Math.min(channel.length, start + bucketSize);
          for (let i = start; i < end; i++) {
            const v = channel[i];
            if (v < mn) mn = v;
            if (v > mx) mx = v;
          }
          peaks[b * 2] = mn;
          peaks[b * 2 + 1] = mx;
        }
        peaksRef.current = peaks;
        ctx.close().catch(() => {});
        if (!cancelled) setStatus('ready');
      } catch (err) {
        console.debug('waveform decode failed', err);
        if (!cancelled) setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [audioUrl]);

  // Draw loop: repaints on every animation frame with current playhead.
  useEffect(() => {
    if (status !== 'ready') return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const draw = () => {
      const audio = audioRef.current;
      const peaks = peaksRef.current;
      const dur = durationRef.current || 1;
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      const width = Math.floor(rect.width * dpr);
      const height = Math.floor(rect.height * dpr);
      if (canvas.width !== width) canvas.width = width;
      if (canvas.height !== height) canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, width, height);

      // Loop region background
      if (loopStartTime != null && loopEndTime != null && loopEndTime > loopStartTime) {
        const x1 = (loopStartTime / dur) * width;
        const x2 = (loopEndTime / dur) * width;
        ctx.fillStyle = 'rgba(234, 88, 12, 0.16)';
        ctx.fillRect(x1, 0, x2 - x1, height);
      }

      // Peaks
      const buckets = peaks.length / 2;
      const mid = height / 2;
      const currentT = audio ? audio.currentTime : 0;
      const playedX = (currentT / dur) * width;

      for (let b = 0; b < buckets; b++) {
        const x = (b / buckets) * width;
        const mn = peaks[b * 2];
        const mx = peaks[b * 2 + 1];
        const yTop = mid + mn * mid;      // mn is negative
        const yBot = mid + mx * mid;
        ctx.fillStyle = x <= playedX ? '#f97316' : '#57534e'; // orange-500 / stone-600
        ctx.fillRect(x, yTop, Math.max(1, dpr), Math.max(1, yBot - yTop));
      }

      // Playhead
      ctx.fillStyle = '#fbbf24'; // amber-400
      ctx.fillRect(playedX - dpr, 0, dpr * 2, height);

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [status, audioRef, loopStartTime, loopEndTime]);

  const seekFromEvent = (e) => {
    if (seekDisabled) return;
    const canvas = canvasRef.current;
    const audio = audioRef.current;
    if (!canvas || !audio || !durationRef.current) return;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const t = ratio * durationRef.current;
    try { audio.currentTime = t; } catch { /* ignore */ }
    if (onSeek) onSeek(t);
  };

  return (
    <div className="mt-3" data-testid="waveform-wrap">
      {status === 'loading' && (
        <div className="text-[10px] text-stone-500 uppercase tracking-widest mb-1">Chargement waveform…</div>
      )}
      {status === 'error' && (
        <div className="text-[10px] text-stone-500 uppercase tracking-widest mb-1">Waveform indisponible</div>
      )}
      <canvas
        ref={canvasRef}
        data-testid="waveform-canvas"
        onClick={seekFromEvent}
        onTouchStart={seekFromEvent}
        className={`w-full h-16 rounded-md bg-stone-950/50 border border-stone-800 ${
          seekDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
        } ${status !== 'ready' ? 'opacity-40' : ''}`}
      />
    </div>
  );
}
